import { environment, Environment } from "app/environment/environment";
import { fetchAuthSession } from "aws-amplify/auth";

type EnvironmentVariableName =
  | "VITE_BACKEND_ENV"
  | "VITE_USE_LOCAL_TRAINMAP_BACKEND"
  | "VITE_ENABLED_FEATURE_FLAGS"
  | "VITE_CLOSEABLE_OPLOGG";

// Is called "Safe" since the environment variable is optional, not required
export function getEnvParsedSafe(variableName: EnvironmentVariableName) {
  const envVariable: string | undefined = import.meta.env[variableName];
  return envVariable ? JSON.parse(envVariable) : undefined;
}

// Is called "Safe" since the environment variable is optional, not required
export function getEnvBoolSafe(variableName: EnvironmentVariableName) {
  const envVariable: string | undefined = import.meta.env[variableName];
  return envVariable?.toLowerCase() === "true";
}

export function getEnvBool(variableName: EnvironmentVariableName) {
  const envVariable: string | undefined = import.meta.env[variableName];
  if (!envVariable) {
    throw new Error(
      `Missing required environment variable ${variableName}. See README.md for instructions.`,
    );
  }
  return envVariable.toLowerCase() === "true";
}

// Specifically for getting backend env (dev/test/stage/prod). Defaults to test
export function getBackendEnvParsedSafe(variableName: EnvironmentVariableName) {
  const envVariable: string = import.meta.env[variableName];
  return ["dev", "test", "stage", "prod"].includes(envVariable)
    ? envVariable
    : "test";
}

/**
 * Get access token for current session from local storage.
 * Attempts to refresh the access token if expired and a valid refresh token is available.
 *
 * @returns Access token as string.
 * @throws Error if access token is undefined or an error occurred during token refresh.
 */
export const getAccessToken = async () => {
  try {
    const { accessToken } = (await fetchAuthSession()).tokens ?? {};
    if (accessToken !== undefined) return accessToken.toString();
  } catch {
    /* Ignore exception and throw new error */
  }
  throw new Error("Could not refresh auth session");
};

export const getBackendUrl = () => {
  const env: Environment = environment();

  switch (env) {
    case Environment.Development:
      return getBackendEnvParsedSafe("VITE_BACKEND_ENV") === "dev"
        ? "http://localhost:8080/drops-backend"
        : `https://services.${getBackendEnvParsedSafe("VITE_BACKEND_ENV")}.trafficgui.vydev.io/drops-backend`;

    case Environment.Test:
      return "https://services.test.trafficgui.vydev.io/drops-backend";

    case Environment.Stage:
      return "https://services.stage.trafficgui.vydev.io/drops-backend";

    case Environment.Production:
      return "https://services.trafficgui.vydev.io/drops-backend";

    case Environment.Unknown:
      return window.location.origin;
  }
};

export const getTrafficguiBaseUrl = () => {
  const env: Environment = environment();
  const switchEnv = getBackendEnvParsedSafe("VITE_BACKEND_ENV");

  switch (env) {
    case Environment.Development:
      return getEnvBoolSafe("VITE_USE_LOCAL_TRAINMAP_BACKEND")
        ? "http://localhost:8081"
        : // There is no remote trainmap for dev, use test instead
          `https://services.${switchEnv === "dev" ? "test" : switchEnv}.trafficgui.vydev.io`;

    case Environment.Test:
      return "https://services.test.trafficgui.vydev.io";

    case Environment.Stage:
      return "https://services.stage.trafficgui.vydev.io";

    case Environment.Production:
      return "https://services.trafficgui.vydev.io";

    case Environment.Unknown:
      return window.location.origin;
  }
};

export const getIncludedPuzzelGroupNames = (): string[] => {
  const env: Environment = environment();
  switch (env) {
    case Environment.Test:
      return ["Alle agenter", "test_sg23_6etg", "Admin"];
    case Environment.Production:
      return ["Alle agenter"];
    case Environment.Development:
      return ["Alle agenter", "test_sg23_6etg", "Admin"];

    case Environment.Stage:
    case Environment.Unknown:
      return ["Alle agenter", "test_sg23_6etg", "Admin"];
  }
};

export const getDriftsinfoBaseUrl = () => {
  const env = environment();

  switch (env) {
    case Environment.Development:
    case Environment.Test:
      return `https://driftsinfo.test.trafficcontrol.vydev.io`;

    case Environment.Stage:
      return `https://driftsinfo.stage.trafficcontrol.vydev.io`;

    case Environment.Production:
      return "https://driftsinfo.vy.no";

    case Environment.Unknown:
      return window.location.origin;
  }
};
